import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BBZEL7EG.js";
import axios from "axios";
import { extractWalletRef } from "./utils.js";
import { SESSION_COOKIE_HEADER_NAME, VERSION_HEADER_NAME, PARTNER_ID_HEADER_NAME, API_KEY_HEADER_NAME } from "./consts.js";
import { ParaApiError } from "./error.js";
const handleResponseSuccess = (response) => {
  if (response.status === 200) {
    return response;
  }
  throw new ParaApiError("Invalid status code");
};
const handleResponseError = (error) => {
  var _a, _b, _c;
  if (error === null) throw new ParaApiError("Error is null");
  if (axios.isAxiosError(error)) {
    let message = (_b = (_a = error.response) == null ? void 0 : _a.data) != null ? _b : "Unknown error";
    if (error.code === "ERR_NETWORK") {
      message = "Connection error";
    } else if (error.code === "ERR_CANCELED") {
      message = "Connection canceled";
    }
    throw new ParaApiError(message, error.code, error.response.status, (_c = error.request) == null ? void 0 : _c.responseURL);
  }
  throw new ParaApiError("Unknown error");
};
class Client {
  constructor({
    userManagementHost,
    apiKey,
    partnerId,
    version,
    opts,
    retrieveSessionCookie,
    persistSessionCookie
  }) {
    this.createUser = (body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users`, body);
      return res.data;
    });
    this.checkUserExists = (auth) => __async(this, null, function* () {
      const res = yield this.baseRequest.get("/users/exists", {
        params: __spreadValues({}, auth)
      });
      return res;
    });
    this.verifyTelegram = (authObject) => __async(this, null, function* () {
      return (yield this.baseRequest.post("/users/telegram", {
        authObject
      })).data;
    });
    this.externalWalletLogin = (body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/external-wallets/login`, body);
      return res.data;
    });
    // POST /users/:userId/verify-email
    this.verifyEmail = (userId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/verify-email`, body);
      return res;
    });
    this.verifyPhone = (userId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/verify-identifier`, body);
      return res;
    });
    this.verifyExternalWallet = (userId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/external-wallets/verify`, body);
      return res;
    });
    // POST /users/:userId/biometrics/key
    this.addSessionPublicKey = (userId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/biometrics/key`, body);
      return res;
    });
    // GET /users/:userId/biometrics/keys
    this.getSessionPublicKeys = (userId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/biometrics/keys`);
      return res;
    });
    // GET /biometrics/location-hints
    this.getBiometricLocationHints = (params) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/biometrics/location-hints`, { params });
      return res.data.hints;
    });
    // GET /users/:userId/biometrics/:biometricId
    this.getSessionPublicKey = (userId, biometricId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/biometrics/${biometricId}`);
      return res;
    });
    // PATCH /users/:userId/biometrics/:biometricId
    this.patchSessionPublicKey = (partnerId, userId, biometricId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.patch(`/users/${userId}/biometrics/${biometricId}`, body, {
        headers: {
          [PARTNER_ID_HEADER_NAME]: partnerId
        }
      });
      return res;
    });
    // GET /biometrics/challenge?email&publicKey
    this.getWebChallenge = (auth) => __async(this, null, function* () {
      const res = yield this.baseRequest.get("/biometrics/challenge", {
        params: __spreadValues({}, auth || {})
      });
      return res.data;
    });
    // POST /touch
    this.touchSession = (regenerate) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(
        `/touch?regenerate=${!!regenerate}`
      );
      return res;
    });
    // GET /session/origin
    this.sessionOrigin = (sessionLookupId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/sessions/${sessionLookupId}/origin`);
      return res.data;
    });
    // POST /biometrics/verify
    this.verifyWebChallenge = (partnerId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/biometrics/verify`, body, {
        headers: {
          [PARTNER_ID_HEADER_NAME]: partnerId
        }
      });
      return res;
    });
    // GET /users/:userId/biometrics/challenge
    this.getSessionChallenge = (userId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/biometrics/challenge`);
      return res;
    });
    // POST /users/:userId/biometrics/verify
    this.verifySessionChallenge = (userId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/biometrics/verify`, body);
      return res;
    });
    // POST /users/:userId/wallets
    this.createWallet = (userId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets`, body);
      return res.data;
    });
    // POST /wallets/pregen
    this.createPregenWallet = (body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/wallets/pregen`, body);
      return res.data;
    });
    // GET /wallets/pregen?pregenIdentifier={pregenIdentifier}&pregenIdentifierType={pregenIdentifierType}
    this.getPregenWallets = (pregenIds, isPortal = false, userId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get("/wallets/pregen", {
        params: { ids: pregenIds, expand: isPortal, userId }
      });
      return res.data;
    });
    // POST /wallets/pregen/claim
    this.claimPregenWallets = (body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/wallets/pregen/claim`, body);
      return res.data;
    });
    // POST /users/:userId/wallets/:walletId/transactions/send
    this.sendTransaction = (userId, walletId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/transactions/send`, body);
      return res;
    });
    // functionality changed to only sign transactions and not send them
    // POST /users/:userId/wallets/:walletId/transactions/sign
    this.signTransaction = (userId, walletId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/transactions/sign`, body);
      return res;
    });
    // POST /users/:userId/wallets/:walletId/refresh
    this.refreshKeys = (userId, walletId, oldPartnerId, newPartnerId, keyShareProtocolId) => __async(this, null, function* () {
      const body = { oldPartnerId, newPartnerId, keyShareProtocolId };
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/refresh`, body);
      return res;
    });
    // PATCH /wallets/pregen/:walletId
    this.updatePregenWallet = (walletId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.patch(`/wallets/pregen/${walletId}`, body);
      return res.data;
    });
    // GET /users/:userId/wallets
    this.getWallets = (userId, includePartnerData) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(
        `/users/${userId}/wallets${includePartnerData ? `?includePartnerData=${encodeURIComponent(includePartnerData)}` : ""}`
      );
      return res;
    });
    // GET /users/:userId/all-wallets
    this.getAllWallets = (userId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/all-wallets`);
      return res;
    });
    // POST /users/:userId/wallets/set
    this.setCurrentWalletIds = (userId, walletIds, needsWallet = false, sessionLookupId, newDeviceSessionLookupId) => __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/set`, {
        walletIds,
        needsWallet,
        sessionLookupId,
        newDeviceSessionLookupId
      });
      return res;
    });
    // POST /login
    this.login = (props) => __async(this, null, function* () {
      const body = props;
      const res = yield this.baseRequest.post("/login", body);
      return res;
    });
    // POST /login
    this.verifyLogin = (verificationCode) => __async(this, null, function* () {
      const body = { verificationCode };
      const res = yield this.baseRequest.post("/login/verify-email", body);
      return res;
    });
    // GET /logout
    this.logout = () => __async(this, null, function* () {
      const res = yield this.baseRequest.get("/logout");
      return res;
    });
    // POST /recovery/verification
    this.recoveryVerification = (email, verificationCode) => __async(this, null, function* () {
      const body = { email, verificationCode };
      const res = yield this.baseRequest.post("/recovery/verification", body);
      return res;
    });
    // POST /recovery
    this.recoveryInit = (email) => __async(this, null, function* () {
      const body = { email };
      const res = yield this.baseRequest.post("/recovery", body);
      return res;
    });
    this.preSignMessage = (userId, walletId, message, scheme, cosmosSignDoc, protocolId) => __async(this, null, function* () {
      const body = { message, scheme, cosmosSignDoc, protocolId };
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/messages/sign`, body);
      return res.data;
    });
    //DELETE /users/:userId
    this.deleteSelf = (userId) => __async(this, null, function* () {
      const res = yield this.baseRequest.delete(`/users/${userId}`);
      return res;
    });
    // GET /users/:userId/wallets/:walletId/capsule-share
    this.getParaShare = (userId, walletId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/wallets/${walletId}/capsule-share`);
      return res.data.share;
    });
    // GET /users/:userId/wallets/:walletId/download-backup-kit
    this.getBackupKit = (userId, walletId) => __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/wallets/${walletId}/download-backup-kit`, {
        responseType: "blob"
      });
      return res;
    });
    // PATCH /users/:userId/biometrics/:biometricId
    this.patchSessionPassword = (partnerId, userId, passwordId, body) => __async(this, null, function* () {
      const res = yield this.baseRequest.patch(`/users/${userId}/passwords/${passwordId}`, body, {
        headers: {
          [PARTNER_ID_HEADER_NAME]: partnerId
        }
      });
      return res;
    });
    // GET /users/:userId/wallets/:walletId/balance
    this.getWalletBalance = (_0) => __async(this, [_0], function* ({
      userId,
      walletId,
      rpcUrl
    }) {
      const res = yield this.baseRequest.get(`/users/${userId}/wallets/${walletId}/balance`, {
        params: {
          rpcUrl
        }
      });
      return res.data;
    });
    this.trackError = (opts) => __async(this, null, function* () {
      yield this.baseRequest.post("/errors/sdk", opts);
    });
    this.trackReactSdkAnalytics = (opts) => __async(this, null, function* () {
      yield this.baseRequest.post("/partners/analytics/react-sdk", opts);
    });
    const headers = __spreadValues(__spreadValues({}, apiKey && { [API_KEY_HEADER_NAME]: apiKey }), partnerId && { [PARTNER_ID_HEADER_NAME]: partnerId });
    const axiosConfig = {
      baseURL: userManagementHost,
      withCredentials: true,
      headers
    };
    if (retrieveSessionCookie) {
      const defaultTransformRequest = Array.isArray(axios.defaults.transformRequest) ? axios.defaults.transformRequest : [axios.defaults.transformRequest];
      axiosConfig.transformRequest = [
        function(data, headers2) {
          const currentSessionCookie = retrieveSessionCookie();
          if (currentSessionCookie) {
            headers2[SESSION_COOKIE_HEADER_NAME] = currentSessionCookie;
          }
          if (version) {
            headers2[VERSION_HEADER_NAME] = version;
          }
          return data;
        },
        ...defaultTransformRequest
      ];
    }
    if (persistSessionCookie) {
      const defaultTransformResponse = Array.isArray(axios.defaults.transformResponse) ? axios.defaults.transformResponse : [axios.defaults.transformResponse];
      axiosConfig.transformResponse = [
        ...defaultTransformResponse,
        function(data, headers2, _status) {
          if (headers2 == null ? void 0 : headers2[SESSION_COOKIE_HEADER_NAME]) {
            persistSessionCookie(headers2[SESSION_COOKIE_HEADER_NAME]);
          }
          return data;
        }
      ];
    }
    this.baseRequest = axios.create(axiosConfig);
    if (opts == null ? void 0 : opts.useFetchAdapter) {
      axios.defaults.adapter = function(config) {
        return fetch(config.baseURL + config.url.substring(1), {
          method: config.method,
          headers: config.headers,
          body: config.data,
          credentials: config.withCredentials ? "include" : void 0
        }).then(
          (response) => response.text().then((text) => ({
            data: text,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            config,
            request: fetch
          }))
        ).catch(function(reason) {
          throw reason;
        });
      };
    }
    this.baseRequest.interceptors.response.use(handleResponseSuccess, handleResponseError);
  }
  // DEPRECATED: use uploadUserKeyShares instead
  // POST /users/:userId/wallets/:walletId/key-shares
  uploadKeyshares(userId, walletId, encryptedKeyshares) {
    return __async(this, null, function* () {
      const body = { keyShares: encryptedKeyshares };
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/key-shares`, body);
      return res;
    });
  }
  // POST /users/:userId/key-shares
  uploadUserKeyShares(userId, encryptedKeyshares) {
    return __async(this, null, function* () {
      const body = { keyShares: encryptedKeyshares };
      const res = yield this.baseRequest.post(`/users/${userId}/key-shares`, body);
      return res;
    });
  }
  // GET /users/:userId/wallets/:walletId/key-shares
  getKeyshare(userId, walletId, type, encryptor) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(
        `/users/${userId}/wallets/${walletId}/key-shares?type=${type}${encryptor ? `&encryptor=${encryptor}` : ""}`
      );
      return res;
    });
  }
  // GET /users/:userId/biometrics/key-shares
  getBiometricKeyshares(userId, biometricPublicKey, getAll) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(
        `/users/${userId}/biometrics/key-shares?publicKey=${biometricPublicKey}&all=${!!getAll}`
      );
      return res;
    });
  }
  // GET /users/:userId/key-shares
  getPasswordKeyshares(userId, passwordId, getAll) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(
        `/users/${userId}/passwords/key-shares?passwordId=${passwordId}&all=${!!getAll}`
      );
      return res;
    });
  }
  // POST '/users/:userId/temporary-shares',
  uploadTransmissionKeyshares(userId, shares) {
    return __async(this, null, function* () {
      const body = { shares };
      const res = yield this.baseRequest.post(`/users/${userId}/temporary-shares`, body);
      return res;
    });
  }
  // GET /users/:userId/temporary-shares returns { temporaryShares: { userId: string, walletId: string, encryptedShare: string, encryptedKey?: string }[] }
  getTransmissionKeyshares(userId, sessionLookupId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/temporary-shares?sessionLookupId=${sessionLookupId}`);
      return res;
    });
  }
  // POST '/users/:userId/resend-verification-code
  resendVerificationCode(_a) {
    return __async(this, null, function* () {
      var _b = _a, { userId } = _b, rest = __objRest(_b, ["userId"]);
      const res = yield this.baseRequest.post(`/users/${userId}/resend-verification-code`, rest);
      return res;
    });
  }
  // POST '/users/:userId/resend-verification-code-by-phone
  resendVerificationCodeByPhone(_c) {
    return __async(this, null, function* () {
      var _d = _c, { userId } = _d, rest = __objRest(_d, ["userId"]);
      const res = yield this.baseRequest.post(`/users/${userId}/resend-verification-code-by-phone`, rest);
      return res;
    });
  }
  // POST recovery/cancel
  cancelRecoveryAttempt(email) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/recovery/cancel`, { email });
      return res;
    });
  }
  // GET '/2fa/users/:userId/check-status'
  check2FAStatus(userId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/2fa/users/${userId}/check-status`);
      return res;
    });
  }
  // POST '/2fa/users/:userId/enable'
  enable2FA(userId, verificationCode) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/2fa/users/${userId}/enable`, { verificationCode });
      return res;
    });
  }
  // POST '/2fa/users/:userId/setup'
  setup2FA(userId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/2fa/users/${userId}/setup`);
      return res;
    });
  }
  // POST /recovery/init
  initializeRecovery(email) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/recovery/init`, { email });
      return res;
    });
  }
  // POST /auth/farcaster/init
  initializeFarcasterLogin() {
    return __async(this, arguments, function* ({ deeplinkUrl } = {}) {
      const res = yield this.baseRequest.post(`/auth/farcaster/init`, { deeplinkUrl });
      return res.data;
    });
  }
  // POST /auth/farcaster/status
  getFarcasterAuthStatus() {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/auth/farcaster/status`);
      return res;
    });
  }
  // POST /recovery/init
  initializeRecoveryForPhone(phone, countryCode) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/recovery/init`, { phone, countryCode });
      return res;
    });
  }
  // POST /recovery/users/:userId/wallets/:walletId/finish
  finalizeRecovery(userId, walletId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/recovery/users/${userId}/wallets/${walletId}/finish`);
      return res;
    });
  }
  // GET /recovery/users/:userId/wallets/:walletId/key-shares
  recoverUserShares(userId, walletId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/recovery/users/${userId}/wallets/${walletId}/key-shares?type=USER&encryptor=RECOVERY`);
      return res;
    });
  }
  // POST /recovery/verify-email
  verifyEmailForRecovery(email, verificationCode) {
    return __async(this, null, function* () {
      const body = { email, verificationCode };
      const res = yield this.baseRequest.post(`/recovery/verify-email`, body);
      return res;
    });
  }
  // POST /recovery/verify-identifier
  verifyPhoneForRecovery(phone, countryCode, verificationCode) {
    return __async(this, null, function* () {
      const body = { phone, countryCode, verificationCode };
      const res = yield this.baseRequest.post(`/recovery/verify-identifier`, body);
      return res;
    });
  }
  // POST /2fa/verify
  verify2FA(email, verificationCode) {
    return __async(this, null, function* () {
      const body = { email, verificationCode };
      const res = yield this.baseRequest.post("/2fa/verify", body);
      return res;
    });
  }
  // POST /2fa/phone/verify
  verify2FAForPhone(phone, countryCode, verificationCode) {
    return __async(this, null, function* () {
      const body = { phone, countryCode, verificationCode };
      const res = yield this.baseRequest.post("/2fa/verify", body);
      return res;
    });
  }
  tempTrasmissionInit(message, userId) {
    return __async(this, null, function* () {
      const body = { message, userId };
      const res = yield this.baseRequest.post("/temporary-transmissions", body);
      return res;
    });
  }
  tempTrasmission(id) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/temporary-transmissions/${id}`);
      return res;
    });
  }
  getPartner(partnerId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/partners/${partnerId}`);
      return res;
    });
  }
  acceptScopes(userId, walletId, body) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/scopes/accept`, body);
      return res;
    });
  }
  getPendingTransaction(userId, pendingTransactionId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/pending-transactions/${pendingTransactionId}`);
      return res;
    });
  }
  acceptPendingTransaction(userId, pendingTransactionId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/pending-transactions/${pendingTransactionId}/accept`);
      return res;
    });
  }
  getOnRampConfig() {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/on-ramp-config`);
      return res.data;
    });
  }
  createOnRampPurchase(_e) {
    return __async(this, null, function* () {
      var _f = _e, {
        userId,
        params
      } = _f, walletParams = __objRest(_f, [
        "userId",
        "params"
      ]);
      const [key, identifier] = extractWalletRef(walletParams);
      const walletString = key === "walletId" ? `wallets/${identifier}` : `external-wallets/${identifier}`;
      const res = yield this.baseRequest.post(`/users/${userId}/${walletString}/purchases`, params);
      return res.data;
    });
  }
  updateOnRampPurchase(_g) {
    return __async(this, null, function* () {
      var _h = _g, {
        userId,
        purchaseId,
        updates
      } = _h, params = __objRest(_h, [
        "userId",
        "purchaseId",
        "updates"
      ]);
      const [key, identifier] = extractWalletRef(params);
      const walletString = key === "walletId" ? `wallets/${identifier}` : `external-wallets/${identifier}`;
      const res = yield this.baseRequest.patch(
        `/users/${userId}/${walletString}/purchases/${purchaseId}`,
        updates
      );
      return res.data;
    });
  }
  getOnRampPurchase(_i) {
    return __async(this, null, function* () {
      var _j = _i, {
        userId,
        purchaseId
      } = _j, params = __objRest(_j, [
        "userId",
        "purchaseId"
      ]);
      const [key, identifier] = extractWalletRef(params);
      const walletString = key === "walletId" ? `wallets/${identifier}` : `external-wallets/${identifier}`;
      const res = yield this.baseRequest.get(`/users/${userId}/${walletString}/purchases/${purchaseId}`);
      return res;
    });
  }
  signMoonPayUrl(_0, _1) {
    return __async(this, arguments, function* (userId, {
      url,
      type,
      cosmosPrefix,
      testMode,
      walletId,
      externalWalletAddress
    }) {
      const walletString = walletId ? `wallets/${walletId}` : `external-wallets/${externalWalletAddress}`;
      const res = yield this.baseRequest.post(`/users/${userId}/${walletString}/moonpay-sign`, {
        url,
        type,
        cosmosPrefix,
        testMode
      });
      return res;
    });
  }
  generateOffRampTx(_0, _1) {
    return __async(this, arguments, function* (userId, {
      provider,
      chainId,
      contractAddress,
      testMode,
      walletId,
      walletType,
      destinationAddress,
      sourceAddress,
      assetQuantity
    }) {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/offramp-generate`, {
        provider,
        testMode,
        chainId,
        contractAddress,
        walletId,
        walletType,
        destinationAddress,
        sourceAddress,
        assetQuantity
      });
      return res.data;
    });
  }
  sendOffRampTx(_0, _1) {
    return __async(this, arguments, function* (userId, {
      tx,
      signature,
      sourceAddress,
      network,
      walletId,
      walletType
    }) {
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/offramp-send`, {
        tx,
        signature,
        sourceAddress,
        network,
        walletType
      });
      return res.data;
    });
  }
  distributeParaShare(_k) {
    return __async(this, null, function* () {
      var _l = _k, {
        userId,
        walletId
      } = _l, rest = __objRest(_l, [
        "userId",
        "walletId"
      ]);
      const body = rest;
      const res = yield this.baseRequest.post(`/users/${userId}/wallets/${walletId}/capsule-share/distribute`, body);
      return res;
    });
  }
  keepSessionAlive(userId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/session/keep-alive`);
      return res.data;
    });
  }
  persistRecoveryPublicKeys(userId, publicKeys) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/recovery-public-keys`, { publicKeys });
      return res.data;
    });
  }
  getRecoveryPublicKeys(userId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/recovery-public-keys`);
      return res.data;
    });
  }
  uploadEncryptedWalletPrivateKey(userId, encryptedWalletPrivateKey, encryptionKeyHash, biometricPublicKey, passwordId) {
    return __async(this, null, function* () {
      const body = { encryptedWalletPrivateKey, encryptionKeyHash, biometricPublicKey, passwordId };
      const res = yield this.baseRequest.post(`/users/${userId}/encrypted-wallet-private-keys`, body);
      return res.data;
    });
  }
  getEncryptedWalletPrivateKeys(userId, encryptionKeyHash) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/encrypted-wallet-private-keys/${encryptionKeyHash}`);
      return res.data;
    });
  }
  getConversionRate(chainId, symbol, currency) {
    return __async(this, null, function* () {
      const params = { symbol, currency };
      const res = yield this.baseRequest.get(`/chains/${chainId}/conversion-rate`, { params });
      return res.data;
    });
  }
  getGasEstimate(chainId, totalGasPrice) {
    return __async(this, null, function* () {
      const params = { totalGasPrice };
      const res = yield this.baseRequest.get(`/chains/${chainId}/gas-estimate`, { params });
      return res.data;
    });
  }
  getGasOracle(chainId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/chains/${chainId}/gas-oracle`);
      return res.data;
    });
  }
  // GET /users/:userId/wallets/:walletId/refresh-done
  isRefreshDone(userId, walletId, partnerId, protocolId) {
    return __async(this, null, function* () {
      const query = new URLSearchParams(__spreadValues(__spreadValues({}, partnerId ? { partnerId } : {}), protocolId ? { protocolId } : {})).toString();
      const res = yield this.baseRequest.get(`/users/${userId}/wallets/${walletId}/refresh-done?${query}`);
      return res.data;
    });
  }
  deletePendingTransaction(userId, pendingTransactionId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.delete(`/users/${userId}/pending-transactions/${pendingTransactionId}`);
      return res.data;
    });
  }
  // POST /users/:userId/passwords/key
  addSessionPasswordPublicKey(userId, body) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/users/${userId}/passwords/key`, body);
      return res;
    });
  }
  getSupportedAuthMethods(auth) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get("/users/supported-auth-methods", {
        params: __spreadValues({}, auth)
      });
      return res.data;
    });
  }
  getPasswords(auth) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get("/users/passwords", {
        params: __spreadValues({}, auth)
      });
      return res.data.passwords;
    });
  }
  // POST /passwords/verify
  verifyPasswordChallenge(partnerId, body) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.post(`/passwords/verify`, body, {
        headers: {
          [PARTNER_ID_HEADER_NAME]: partnerId
        }
      });
      return res;
    });
  }
  getEncryptedWalletPrivateKey(passwordId) {
    return __async(this, null, function* () {
      const query = new URLSearchParams({ passwordId }).toString();
      const res = yield this.baseRequest.get(`/encrypted-wallet-private-keys?${query}`);
      return res;
    });
  }
  // GET /users/:userId
  getUser(userId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}`);
      return res.data;
    });
  }
  // GET /users/:userId/accounts
  getAccountMetadata(userId, partnerId) {
    return __async(this, null, function* () {
      const res = yield this.baseRequest.get(`/users/${userId}/oauth/accounts`, {
        params: { partnerId }
      });
      return {
        accountMetadata: Object.entries(res.data.accountMetadata || {}).reduce(
          (acc, [method, obj]) => __spreadProps(__spreadValues({}, acc), {
            [method]: __spreadProps(__spreadValues({}, obj), {
              date: new Date(obj.date)
            })
          }),
          {}
        )
      };
    });
  }
}
var client_default = Client;
export {
  client_default as default,
  handleResponseError,
  handleResponseSuccess
};
